import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StorageManagerService } from './storage-manager.service';
import { ThemeDetection, ThemeDetectionResponse } from '@ionic-native/theme-detection/ngx';
import { Platform } from '@ionic/angular';
import { StatusBar, Style } from '@capacitor/status-bar';
import { FeedbackService, VibrateType } from './feedback.service';
import { AppIcon } from '@capacitor-community/app-icon';
import { NavigationBar } from "@hugotomazi/capacitor-navigation-bar"
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';

@Injectable({
  providedIn: 'root'
})
export class DarkmodeService {

  public _darkmode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null)
  private _accentColor: string
  private _accentColorRGB: {r: number, g:number, b: number}

  public refreshBackdropFilter: boolean = true

  constructor(
    private storageManager: StorageManagerService,
    private themeDetection: ThemeDetection,
    private platform: Platform,
    private ngZone: NgZone,
    private feedback: FeedbackService
    ) {
    
  }
   public get isDarkMode(){
    return this._darkmode.asObservable()
   }

   public get isDarkModeValue(){
     return this._darkmode.getValue()
   }
   public get accentColor(): string{
    return this._accentColor
   }
   public get accentColorRGB(): {r: number, g:number, b: number}{
    return this._accentColorRGB
   }

   public async setDarkmode(to: "light" | "dark"){
      if(to === 'light'){
        await this.storageManager.setItem<string>("darkmode", "light")
        this._darkmode.next(false)
        await this.setStatusBar(false)
        document.body.classList.toggle('dark', false);
        this.setRefreshBackdropFilter()
      }else if(to === 'dark'){
        await this.storageManager.setItem<string>("darkmode", "dark")
        this._darkmode.next(true)
        await this.setStatusBar(true)
        document.body.classList.toggle('dark', true);
        this.setRefreshBackdropFilter()
      }
   }

   //damit elemente mit backdropfilter richtig rendern 
   private setRefreshBackdropFilter(){
    this.ngZone.run(() => {
      this.refreshBackdropFilter = false
      setTimeout(() => this.refreshBackdropFilter = true , 400);
    })
   }

  public async setStartupMode(){
    const storageValue = await this.storageManager.getItem<string>("darkmode")
    this.setAccentColor(await this.storageManager.getItem<string>("ACCENT_COLOR") || "#6576AD")
    if(storageValue == null){
      if(this.platform.is("capacitor")){
        const isAvailable: ThemeDetectionResponse = await this.themeDetection.isAvailable()
        if(isAvailable.value) {
          this._darkmode.next((await this.themeDetection.isDarkModeEnabled()).value)
          this.setStatusBar(this._darkmode.getValue())
        } 
        else {
          this._darkmode.next(false)
          this.setStatusBar(false)
        } 
      }else{
        this._darkmode.next(window.matchMedia("(prefers-color-scheme: dark)").matches);
      }
    }else{
      if (await this.storageManager.getItem<string>("darkmode") === "light"){
        this._darkmode.next(false)
        await this.setStatusBar(false)
      }
      else{
        this._darkmode.next(true)
        await this.setStatusBar(true)
      }
    } 
  }

  private async setStatusBar(dark: boolean){

    if(this.platform.is("capacitor")) {
      this.ngZone.run(async () => {
        if(dark) await Promise.all([
          StatusBar.setBackgroundColor({color: "#272932"}),
          NavigationBar.setColor({color: "#272932"}),
          StatusBar.setStyle({style: Style.Dark}),
          EdgeToEdge.setBackgroundColor({color: "#272932"})
        ])
        else await Promise.all([
          StatusBar.setBackgroundColor({color: "#ECEEF2"}),
          NavigationBar.setColor({color: "#ECEEF2"}),
          StatusBar.setStyle({style: Style.Light}),
          EdgeToEdge.setBackgroundColor({color: "#ECEEF2"})
        ])
      })
    }   
  }
 
  /**
   * Setzt und sspeichert eine Akzentfarbe der Seite
   * @param {string} color
   * @example setAccentColor("#ff0000") 
   */
  public async setAccentColor(color: string){
    document.documentElement.style.setProperty("--blue", color);

    const rgb: {r: number, g:number, b: number} = this.hexToRgb(color)
    document.documentElement.style.setProperty("--blueRGB", `${rgb.r}, ${rgb.g}, ${rgb.b}`);

    //App icon ändern
    if(this.platform.is("capacitor") && this.platform.is("ios") && (await AppIcon.isSupported()).value)
      await AppIcon.reset({ suppressNotification: true })
    
    this._accentColor = color
    this._accentColorRGB = rgb
    document.querySelector("meta[name=theme-color]").setAttribute("content", color);
    this.feedback.vibrate(VibrateType.medium)
    await this.storageManager.setItem<string>("ACCENT_COLOR", color)
  }

  public hexToRgb(hex: string): {r: number, g:number, b: number}{
      var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
  }

}
 